import { useDeferredValue, useMemo, useRef } from 'react'
import { useQuery, ApolloError } from '@apollo/client'
import _get from 'lodash/get'
import { GET_CONTENT_FOR_TILE_HYDRATION } from '../../query'
import { APOLLO_CLIENT_ERROR_POLICY_ALL } from '../../../apollo-graphql/index'
import type { PlaylistContentItem } from '../../types'

/**
 * Per-row tile hydration primitive.
 *
 * The only call site of `GET_CONTENT_FOR_TILE_HYDRATION`. Consumers pass a
 * flat `titleGroupIds` list; the hook handles fanout, previousData
 * fallbacks, and the deferred-value coalescing pass.
 *
 * Variable shape MUST match the legacy `useContentHydration` exactly —
 * Apollo cache keys are variables-derived, so any divergence breaks
 * cache parity across the migration window. The 3-image
 * `CONTENT_IMAGE_LIST` selection is preserved verbatim for the same
 * reason (cache parity > image breadth — `Tile.v2GraphqlManager`'s
 * 11-image list is intentionally NOT mirrored here).
 *
 * `fetchPolicy: 'cache-and-network'` is load-bearing —
 * `featureMedia.userMediaPosition.position` is per-viewer state stored
 * under the parent `Content:<id>` entity (not normalised separately).
 * `cache-first` would pin a returning viewer's progress bar to their
 * last visit's value.
 */
const CONTENT_IMAGE_LIST = [
  'd6-keyart-with-text',
  'd6-keyart-no-text',
  'd6-poster-image',
]

/**
 * Hard upper bound on fanout. Web app uses `createHttpLink` (not
 * `BatchHttpLink`) so each `useQuery` is a separate HTTP request;
 * HTTP/2 multiplexes them but going above 4 hits diminishing returns
 * relative to the per-request overhead Apollo adds.
 */
const MAX_SLOTS = 4

/**
 * Default threshold for engaging fanout. Below this, everything lands
 * in slot 0 and the remaining slots are skipped — a single-query path
 * that matches the legacy behaviour for small lists (Watch Later ~15
 * items, Favorites ~10 items). Above this, the list partitions across
 * up to `maxConcurrent` slots. Tunable per call site — Watch History
 * uses 12 because it paginates in 10-item pages.
 */
const DEFAULT_FANOUT_THRESHOLD = 24

const EMPTY_MAP: ReadonlyMap<string, PlaylistContentItem> = new Map()

interface UseTitleGroupHydrationOptions {
  skip?: boolean
  jwt?: string
  /** Below this, single-query path. Default 24. */
  fanoutThreshold?: number
  /** Upper bound on slots used. Capped at MAX_SLOTS=4. Default 4. */
  maxConcurrent?: number
}

interface UseTitleGroupHydrationResult {
  contentMap: ReadonlyMap<string, PlaylistContentItem>
  loading: boolean
  error: ApolloError | undefined
}

/**
 * Map a `titleGroupId` to one of `slotCount` query slots
 * deterministically via content-hash mod slotCount.
 *
 * Content-based (not position-based) so a given titleGroupId always
 * lands in the same slot regardless of list order — adding or removing
 * one item from a playlist mutates exactly ONE slot's variables.
 * Apollo refetches at most that one slot; the other slots keep their
 * stable `.data` ref. This is the inverse of round-robin, which would
 * shift every index after a deletion and scramble every slot's slice
 * on every `cache.modify` (the legacy `useContentHydration` docblock
 * at L86–104 documents the original rationale; the partition logic is
 * carried forward verbatim here).
 *
 * The plan's §7.1 prescription was "round-robin", but the existing
 * code's docblock explicitly calls round-robin out as a defect that
 * caused all four slots to refetch per delete. Carrying hash-based
 * forward keeps cache.modify behaviour surgical and `previousData`
 * fallback truly just a safety net for the loading window.
 *
 * Plain djb2-ish multiply-accumulate. `| 0` keeps the value in signed
 * 32-bit space so long composite strings don't overflow precision.
 */
// djb2-ish 32-bit coercion. `Math.imul` already wraps to 32-bit signed,
// and the subsequent addition stays inside the safe-integer range for
// any input < 2^31, so a `Math.imul(0, ...) + 0` cycle keeps the
// running hash 32-bit-clean without resorting to a bitwise OR.
function toInt32 (n: number): number {
  return Math.imul(n, 1)
}

function hashSlot (id: string, slotCount: number): number {
  if (slotCount <= 1) return 0
  let h = 0
  for (let i = 0; i < id.length; i += 1) {
    h = toInt32(Math.imul(31, h) + id.charCodeAt(i))
  }
  return ((h % slotCount) + slotCount) % slotCount
}

/**
 * Merge one `contentList.content[]` response into the shared map.
 * Keys by `titleGroupId` with the `${contentId}-${contentType}`
 * fallback that upstream hooks expect — every current shell selects
 * `titleGroupId` directly, but the fallback covers consumers whose
 * items don't carry one (Watch History, In-Progress, the watch-history
 * block on `CommunityProfilePage`).
 */
function mergeContentList (
  target: Map<string, PlaylistContentItem>,
  list: readonly PlaylistContentItem[],
): void {
  list.forEach((entry) => {
    const raw = (entry as { titleGroupId?: string | null })?.titleGroupId
    const fallback = entry?.contentId != null && entry?.contentType
      ? `${String(entry.contentId)}-${String(entry.contentType)}`
      : ''
    const key = raw ? String(raw) : fallback
    if (key) target.set(key, entry)
  })
}

/**
 * Hydrate a flat list of `titleGroupId`s.
 *
 * Returns a `Map<titleGroupId, ContentEntry>`. Order is not guaranteed
 * when Apollo dedupes overlapping ids — always look up by key.
 *
 * Fanout contract:
 *   - `titleGroupIds.length <= fanoutThreshold`: slot 0 owns the full
 *     list, slots 1..N skip. Matches legacy behaviour for small rows.
 *   - `titleGroupIds.length >  fanoutThreshold`: partitioned across
 *     `N = min(maxConcurrent, MAX_SLOTS)` slots via `hashSlot(id) % N`.
 *
 * Loading semantic: OR across non-skipped slots only. Empty / skipped
 * slots contribute nothing to either `loading` or the merged map.
 *
 * Error semantic: first non-falsy slot error wins (matches legacy
 * single-query rendering path).
 */
export default function useTitleGroupHydration (
  titleGroupIds: readonly string[],
  language: string,
  options?: UseTitleGroupHydrationOptions,
): UseTitleGroupHydrationResult {
  const skip = !!options?.skip || titleGroupIds.length === 0
  const fanoutThreshold = options?.fanoutThreshold ?? DEFAULT_FANOUT_THRESHOLD
  const maxConcurrent = Math.min(
    Math.max(1, options?.maxConcurrent ?? MAX_SLOTS),
    MAX_SLOTS,
  )
  const jwt = options?.jwt

  // Decide effective slot count for this call. Below threshold, slot 0
  // owns the full list and the other slots short-circuit via skip.
  const effectiveSlotCount = titleGroupIds.length > fanoutThreshold
    ? maxConcurrent
    : 1

  // Stabilise per-slot slice references. Without this, every render
  // would allocate four new arrays even when content is bit-identical,
  // and the per-slot `vars<N>` memos below would recompute (which
  // makes Apollo treat the variables as changed and refetch).
  const prevSlicesRef = useRef<string[][]>([])
  const slices = useMemo<string[][]>(() => {
    const next: string[][] = []
    for (let i = 0; i < MAX_SLOTS; i += 1) next.push([])
    if (effectiveSlotCount === 1) {
      // Below-threshold path: slot 0 takes everything, slots 1..3 stay
      // empty (and `skip: true` later). Dedupe to match legacy behaviour.
      const seen = new Set<string>()
      for (let i = 0; i < titleGroupIds.length; i += 1) {
        const id = titleGroupIds[i]
        if (id && !seen.has(id)) {
          seen.add(id)
          next[0].push(id)
        }
      }
    } else {
      // Above-threshold: hash-partition across `effectiveSlotCount` slots.
      const seen = new Set<string>()
      for (let i = 0; i < titleGroupIds.length; i += 1) {
        const id = titleGroupIds[i]
        if (id && !seen.has(id)) {
          seen.add(id)
          next[hashSlot(id, effectiveSlotCount)].push(id)
        }
      }
    }
    // Reuse prev slot arrays when bit-identical, so reference equality
    // skips the per-slot `vars<N>` memo recompute downstream.
    const prev = prevSlicesRef.current
    for (let i = 0; i < MAX_SLOTS; i += 1) {
      if (
        prev[i]
        && prev[i].length === next[i].length
        && prev[i].every((v, j) => v === next[i][j])
      ) {
        next[i] = prev[i]
      }
    }
    prevSlicesRef.current = next
    return next
  }, [titleGroupIds, effectiveSlotCount])

  // Per-slot variables. ONE useMemo per slot, deps `[slices[N], language]`
  // — combined with the reference-stable slices above, the memo only
  // rebuilds when its slot's content actually changed. Variables shape
  // MUST match `useContentHydration` byte-for-byte (cache parity).
  // All four declared explicitly so rules-of-hooks linting stays green.
  const vars0 = useMemo(() => ({
    titleGroupIds: slices[0],
    language: [language, '_primary'],
    imageType: CONTENT_IMAGE_LIST,
    learnerLevelLanguage: language,
  }), [slices[0], language])
  const vars1 = useMemo(() => ({
    titleGroupIds: slices[1],
    language: [language, '_primary'],
    imageType: CONTENT_IMAGE_LIST,
    learnerLevelLanguage: language,
  }), [slices[1], language])
  const vars2 = useMemo(() => ({
    titleGroupIds: slices[2],
    language: [language, '_primary'],
    imageType: CONTENT_IMAGE_LIST,
    learnerLevelLanguage: language,
  }), [slices[2], language])
  const vars3 = useMemo(() => ({
    titleGroupIds: slices[3],
    language: [language, '_primary'],
    imageType: CONTENT_IMAGE_LIST,
    learnerLevelLanguage: language,
  }), [slices[3], language])

  // Context identity changes only when jwt changes; stable otherwise.
  const context = useMemo(
    () => (jwt ? { jwt } : undefined),
    [jwt],
  )

  // Four parallel `useQuery` slots — Apollo manages each independently.
  // Active set is gated by `effectiveSlotCount`: slots beyond it are
  // forced to `skip: true` even when their slice is non-empty (they
  // can't be, but the guard documents intent).
  const r0 = useQuery(GET_CONTENT_FOR_TILE_HYDRATION, {
    variables: vars0,
    skip: skip || slices[0].length === 0 || effectiveSlotCount < 1,
    fetchPolicy: 'cache-and-network',
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context,
  })
  const r1 = useQuery(GET_CONTENT_FOR_TILE_HYDRATION, {
    variables: vars1,
    skip: skip || slices[1].length === 0 || effectiveSlotCount < 2,
    fetchPolicy: 'cache-and-network',
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context,
  })
  const r2 = useQuery(GET_CONTENT_FOR_TILE_HYDRATION, {
    variables: vars2,
    skip: skip || slices[2].length === 0 || effectiveSlotCount < 3,
    fetchPolicy: 'cache-and-network',
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context,
  })
  const r3 = useQuery(GET_CONTENT_FOR_TILE_HYDRATION, {
    variables: vars3,
    skip: skip || slices[3].length === 0 || effectiveSlotCount < 4,
    fetchPolicy: 'cache-and-network',
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context,
  })

  // `data ?? previousData` per slot — keeps the merged map populated
  // through Apollo's subset-query loading window (`loading:true,
  // data:undefined` for cache-miss variables sets). Without this, every
  // cache.modify trim drops affected tiles out of the merged map until
  // the slot resolves. Safe to read stale entries because the per-row
  // builder iterates the trimmed shell, not the map directly.
  const eff0 = r0.data ?? r0.previousData
  const eff1 = r1.data ?? r1.previousData
  const eff2 = r2.data ?? r2.previousData
  const eff3 = r3.data ?? r3.previousData

  // Merge each slot's response into one map. Skipped slot →
  // `eff{n} === undefined` → its `_get` returns `[]` → no contribution.
  const contentMap = useMemo<ReadonlyMap<string, PlaylistContentItem>>(() => {
    if (skip) return EMPTY_MAP
    const map = new Map<string, PlaylistContentItem>()
    const datas = [eff0, eff1, eff2, eff3]
    for (let i = 0; i < datas.length; i += 1) {
      const list = _get(datas[i], 'contentList.content', []) as PlaylistContentItem[]
      if (Array.isArray(list)) mergeContentList(map, list)
    }
    return map
  // `titleGroupIds.length` is intentionally NOT in the deps — the merge
  // reads only `datas` from each slot, and an id-count change without a
  // corresponding `eff*` change cannot affect the merged map.
  }, [eff0, eff1, eff2, eff3, skip, effectiveSlotCount])

  // Coalesce rapid slot resolves into one downstream commit. During
  // cold load, slots resolve asynchronously across ~1.8 s; without
  // `useDeferredValue`, each resolve triggers a separate parent
  // re-render. The deferred ref lets React's concurrent scheduler
  // commit at its discretion — temporally adjacent resolves coalesce.
  //
  // Important contract: `loading` below is OR'd with
  // `contentMap !== deferredContentMap` so consumers stay in the
  // "hydrating" branch for the one render where Apollo has resolved
  // (raw loading:false) but the deferred map has not yet caught up.
  // Without that gate, downstream `isHydrating`-gated skeleton paths
  // (e.g. `PlaylistRow.Body`) see `items.length: 0` with
  // `isHydrating: false` for one frame and flash the empty-state
  // branch before the deferred commit lands with the real items.
  //
  // Does NOT alter the `previousData` semantics — operates on the
  // merged map ref, not on Apollo's per-slot state.
  const deferredContentMap = useDeferredValue(contentMap)

  // Active loading is OR across slots that aren't skipped, PLUS the
  // deferred-catchup gate described above. Slots beyond
  // `effectiveSlotCount` are skipped, so they report `loading: false`
  // and don't keep this flag stuck true.
  const queryLoading = (
    (effectiveSlotCount >= 1 && r0.loading)
    || (effectiveSlotCount >= 2 && r1.loading)
    || (effectiveSlotCount >= 3 && r2.loading)
    || (effectiveSlotCount >= 4 && r3.loading)
  )
  const loading = queryLoading || contentMap !== deferredContentMap
  const error = r0.error || r1.error || r2.error || r3.error

  return { contentMap: deferredContentMap, loading, error }
}

export type {
  UseTitleGroupHydrationOptions,
  UseTitleGroupHydrationResult,
}
